import { SignInButton, UserButton, useUser } from "@clerk/clerk-react";
import { Component, type ReactNode } from "react";
import { useConvexAuth, useQuery } from "convex/react";
import { Archive, Clock3, Lock, LogIn, ShieldAlert } from "lucide-react";
import { api } from "../../convex/_generated/api";
import { isConfigured, serviceConfig } from "../config";
import { canManageWorkspaceActions, type WorkspaceMembershipSummary } from "../workspaceRolePermissions";
import { genericAgencyBranding, type AgencyBranding } from "../agencyBranding";

export type ManagerAccessUser = {
  memberships?: Array<WorkspaceMembershipSummary & { organization?: { id: string; [key: string]: unknown } }>;
  branding?: AgencyBranding;
};

export function WorkspaceAccessWaiting({ suspended = false, routingFailed = false }: { suspended?: boolean; routingFailed?: boolean }) {
  const title = suspended ? "Workspace access suspended" : "Workspace access pending";
  const message = suspended
    ? "Your workspace access is suspended. Contact an agency admin to restore access."
    : routingFailed
      ? "We could not route your request automatically. Please contact your agency directly."
      : "Your account is registered and is awaiting admin approval. You will get workspace access once a role is assigned.";

  return (
    <AuthStateSurface
      title={title}
      message={message}
      icon={suspended ? <ShieldAlert /> : <Clock3 />}
      titleId="workspace-access-waiting-title"
      headingLevel="h1"
    >
      <div className="ops-auth-state-account">
        <UserButton />
        <span>Use the account menu to sign out.</span>
      </div>
    </AuthStateSurface>
  );
}

type ManagerAccessChildren = ReactNode | ((currentUser: ManagerAccessUser) => ReactNode);

export function ConvexAuthGate({ children, title }: { children: ReactNode; title: string }) {
  const { isLoaded, isSignedIn } = useUser();
  const convexAuth = useConvexAuth();

  if (!isLoaded || convexAuth.isLoading) {
    return (
      <AuthStateSurface
        title={title}
        message={`Loading ${title.toLowerCase()}.`}
        icon={<Clock3 />}
        messageRole="status"
      />
    );
  }

  if (!isSignedIn || !convexAuth.isAuthenticated) {
    return (
      <AuthStateSurface
        title={title}
        message="Sign in with the account connected to your workspace invitation."
        icon={<Lock />}
      >
        <SignInButton mode="modal">
          <button className="ops-auth-state-primary" type="button">
            <LogIn size={16} aria-hidden="true" />
            Sign in
          </button>
        </SignInButton>
      </AuthStateSurface>
    );
  }

  return <>{children}</>;
}

export function ManagerAccessBoundary({ children }: { children: ManagerAccessChildren }) {
  if (!isConfigured(serviceConfig.convex.url)) {
    return <>{renderManagerAccessChildren(children, {})}</>;
  }

  return <ConfiguredManagerAccessBoundary>{children}</ConfiguredManagerAccessBoundary>;
}

function ConfiguredManagerAccessBoundary({ children }: { children: ManagerAccessChildren }) {
  const currentUser = useQuery(api.identity.getCurrentUserMemberships, {}) as ManagerAccessUser | null | undefined;

  if (currentUser === undefined) {
    return <AdminAccessPanel message="Checking admin access." />;
  }

  if (!canManageWorkspaceActions({ memberships: currentUser?.memberships })) {
    return <AdminAccessPanel message="Admin tools are available to account managers and agency admins." />;
  }

  return <>{renderManagerAccessChildren(children, currentUser ?? {})}</>;
}

function renderManagerAccessChildren(children: ManagerAccessChildren, currentUser: ManagerAccessUser) {
  return typeof children === "function" ? children(currentUser) : children;
}

export class FallbackBoundary extends Component<
  { children: JSX.Element; fallback: JSX.Element | ((error: Error) => JSX.Element) },
  { error?: Error }
> {
  state: { error?: Error } = {};

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return typeof this.props.fallback === "function" ? this.props.fallback(this.state.error) : this.props.fallback;
  }
}

export function WorkspaceAccessUnavailable({ title }: { title: string }) {
  return (
    <AuthStateSurface
      title={title}
      message="This workspace needs a signed-in membership before its data can be shown."
      icon={<Lock />}
      messageRole="alert"
    />
  );
}

export function AdminAccessPanel({ message }: { message: string }) {
  return (
    <AuthStateSurface title="Admin access" message={message} icon={<Archive />} />
  );
}

function AuthStateSurface({
  title,
  message,
  icon,
  children,
  headingLevel = "strong",
  titleId,
  messageRole,
}: {
  title: string;
  message: string;
  icon: ReactNode;
  children?: ReactNode;
  headingLevel?: "h1" | "strong";
  titleId?: string;
  messageRole?: "alert" | "status";
}) {
  const heading = headingLevel === "h1"
    ? <h1 id={titleId}>{title}</h1>
    : <strong id={titleId}>{title}</strong>;

  return (
    <main className="ops-auth-state" aria-labelledby={titleId}>
      <section className="ops-auth-state-lath">
        <div className="ops-auth-state-brand" aria-label={genericAgencyBranding.displayName}><span className="brand-mark">CP</span><span>{genericAgencyBranding.displayName}</span></div>
        <div className="ops-auth-state-icon" aria-hidden="true">{icon}</div>
        <div className="ops-auth-state-copy">
          {heading}
          <p role={messageRole}>{message}</p>
        </div>
        {children ? <div className="ops-auth-state-action">{children}</div> : null}
      </section>
    </main>
  );
}
